import { DynamicResponse } from 'utils/api-utils/api-utils';
import SessionStorage from '../../utils/SessionStorage';
import { useMitekContext } from './MitekProvider';
import { redirect, clearSessionAndRedirectToDataEntry } from './MitekUtils';
import { callAcquisitionBff } from '../../utils/acquisition-api/AcquisitionBff';
import { IApplicationStepUpResponse } from '../../types/responses/ApplicationStepUpResponse';

// Explicit use of 'any' because the types from API responses or errors are not 
// documented.
export type ActionOptions = {
    onFailure?: (response?: DynamicResponse<any>) => void,
    onSuccess?: (response?: DynamicResponse<any>) => void
}

export function useMitekSubmit() {
    const {
        MitekProps,
        MitekConfiguration,
        retrieveResultsToSubmitModern
    } = useMitekContext();

    async function fullMitekSubmit(options?: ActionOptions) {

        const attemptSubmit = async () => {
            const requestObject = retrieveResultsToSubmitModern();
            const response = await callAcquisitionBff<IApplicationStepUpResponse>('stepup-authorization', { 
                body: { stepUpAuthorization: 
                    { 
                        stepUpType: 'MITEK', 
                        ...requestObject,
                    }}
            })
            const flowApplicationData = SessionStorage.tryGetObject('flowApplicationData', null);
            flowApplicationData.applicationStatus = response.data.applicationStatus;
            flowApplicationData.letter = response.data.letter;
            SessionStorage.setObject('flowApplicationData', flowApplicationData);

            if (
                MitekConfiguration.current.isMobileOnlySession
                && response.data.applicationStatus.applicationStatus
            ) {
                options?.onSuccess && options.onSuccess(response);

                switch (response.data.applicationStatus.applicationStatus) {
                    case 'HARDDECLINE':
                    case 'SOFTDECLINE':
                    case 'ERROR':
                        SessionStorage.remove('isCounterOffer');
                        redirect(MitekProps.limitedEsignPageUrl, {
                            withoutBackNavigation: true,
                        });
                        return;
                    case 'APPROVED':
                        redirect(MitekProps.confirmationPageUrl!);
                        break;
                    case 'PENDING':
                        redirect(MitekProps.pendingApprovalPageUrl);
                        break;
                    default:
                        throw 'Unable to resolve submission result'
                }
            } else if (response.status === 200) {
                options?.onSuccess && options.onSuccess(response);
            } else {
                throw { message: 'Failed to Upload', response };
            }

            return;
        }

        try {
            for (let i = MitekProps.mitekRetryAmount ?? 1; i > 0; --i) {
                try {
                    await attemptSubmit();
                } catch (innerError: any) {
                    if (i <= 0 || ('status' in innerError && [401, 404, 500, 501, 502, 503, 504].includes(innerError.status))) {
                        options?.onFailure && options.onFailure((innerError as any)?.response ?? {});
                        throw innerError;
                    }
                }
            }
        } catch {
            clearSessionAndRedirectToDataEntry(
                MitekProps.dataEntryPageUrl,
                MitekProps.errorApiPushdownCode,
            );
        }
        return;
    }

    return {
        fullMitekSubmit,
    };
}

export default useMitekSubmit;
